import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Text, Image, Flex } from '../Base';
import ButtonBase from '../Base/Button/ButtonBase';
import { get } from '../Base/constants';
import getConfig from '../../Util/Config';

const LOGO_SIZE = 24;
const BUTTON_HEIGHT = 56;
const PRESS_OFFSET = 4;

const isVectorLogo = (logoPath) => /\.[a-z0-9]+$/i.test(logoPath);

const getLogoSrc = (logoPath) =>
  isVectorLogo(logoPath) ? `/assets/images/${logoPath}` : `/assets/images/${logoPath}.png`;

const getLogoSrcSet = (logoPath) =>
  isVectorLogo(logoPath)
    ? undefined
    : `/assets/images/${logoPath}-1x.png 1x, /assets/images/${logoPath}-2x.png 2x, /assets/images/${logoPath}-3x.png 3x,`;

/**
 * Outlined button from the eggspace design system
 */
const PassportButton = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${BUTTON_HEIGHT}px;
  border: 2px solid ${get('colors.eggSpace.gray.300')};
  border-radius: 16px;
  background-color: ${get('colors.white')};
  color: ${get('colors.eggSpace.gray.900')};
  font-family: 'IBM Plex Sans Thai Looped', Anuphan, sans-serif;
  font-weight: ${get('fontWeights.bold')};
  box-shadow: 0 ${PRESS_OFFSET}px 0 0 ${get('colors.eggSpace.gray.300')};
  transition: all ${get('duration.fast')};

  @media (hover: hover) {
    &:hover {
      background-color: ${get('colors.eggSpace.gray.200')};
    }
  }

  &:active {
    box-shadow: none;
    transform: translateY(${PRESS_OFFSET}px);
  }

  &:disabled {
    background-color: ${get('colors.eggSpace.gray.100')};
    border-color: ${get('colors.eggSpace.gray.300')};
    box-shadow: none;
  }
`;

const PassportLogin = ({ onPassportLoginClick }) => {
  const { passportProviders } = getConfig().skooldioLogin || {};

  if (!passportProviders) return null;

  // The gap is one step wider than the design's `gap 2` so the 4px drop shadow
  // under each button does not eat into the space between them.
  return (
    <Flex flexDirection={'column'} gridRowGap={3}>
      {passportProviders.map(({ provider: providerName, loginURL, title, logoPath }) => {
        const logoPaths = typeof logoPath === 'string' ? [logoPath] : logoPath;
        const dataTestIdPrefix = providerName ? `btn-passport-${providerName}` : 'btn-passport';

        return (
          <PassportButton
            key={providerName}
            onClick={() => onPassportLoginClick(loginURL, providerName)}
            data-testid={`${dataTestIdPrefix}-login`}
          >
            <Flex alignItems={'center'} mr={2} gridColumnGap={1}>
              {logoPaths.map((logo) => (
                <Image
                  key={`${providerName}:${logo}`}
                  srcSet={getLogoSrcSet(logo)}
                  src={getLogoSrc(logo)}
                  alt={title}
                  width={`${LOGO_SIZE}px`}
                  height={`${LOGO_SIZE}px`}
                />
              ))}
            </Flex>
            <Text textStyle={'subtitle1'}>{title}</Text>
          </PassportButton>
        );
      })}
    </Flex>
  );
};

PassportLogin.propTypes = {
  onPassportLoginClick: PropTypes.func,
};

export default PassportLogin;
